import { useEffect } from 'react';
import Filters from '../components/filter/Filters';
import ReportSection from '../components/reports/ReportSection';
import ReportChart from '../components/reports/ReportChart';
import { transformDataIntoGroups } from '../util/reportDataTransformers';
import _ from 'lodash';
import { useQuery } from 'react-query';
import queryConfig from '../util/queryConfig';
import useApi from '../api';
import Throbber from '../components/throbber/Throbber';
import Error from '../components/error/Error';
import useTenant from '../hooks/useTenant';
import { QueryKey } from '../constants/queryKey';
import usePersistedFilter from '../hooks/usePersistedFilter';

const KpiCard = ({ label, value, subtitle, accent }) => (
    <div className={`kpi-card ${accent ? 'kpi-card--accent' : ''}`}>
        <div className="kpi-card__value">{value ?? '—'}</div>
        <div className="kpi-card__label">{label}</div>
        {subtitle && <div className="kpi-card__subtitle">{subtitle}</div>}
    </div>
);

const ReportsView = () => {
    const { tenant } = useTenant();
    const { getScenarios, getReport } = useApi();
    const [filter, setFilter] = usePersistedFilter('statistics');
    const { isLoading, error, data: scenarios } = useQuery([QueryKey.Scenarios], async () => {
        const response = await getScenarios();
        return response.data;
    }, queryConfig);

    const { data: funnelData } = useQuery(
        [QueryKey.Reports, { slug: 'video_funnel', ...filter }],
        async () => { const r = await getReport('video_funnel', filter); return r.data; },
        { ...queryConfig, enabled: !!filter.year && !!filter.month }
    );

    useEffect(() => {
        if (scenarios?.scenarios?.length) {
            const valid = scenarios.scenarios.some(s => s.scenario_id === filter.scenario);
            if (!filter.scenario || !valid) {
                setFilter(f => ({ ...f, scenario: scenarios.scenarios[0].scenario_id }));
            }
        }
    }, [scenarios]);

    const nrOfDaysInMonth = (year, month) => new Date(year, month, 0).getDate();

    const onFilterChange = (e, prop) => {
        setFilter({ ...filter, [prop]: e.target.value });
    };

    const funnel = funnelData?.rows?.[0] || {};
    const openRate = funnel.produced > 0 ? Math.round(100 * funnel.opened / funnel.produced) : 0;
    const watchedRate = funnel.opened > 0 ? Math.round(100 * funnel.watched / funnel.opened) : 0;
    const completionRate = funnel.watched > 0 ? Math.round(100 * funnel.completed / funnel.watched) : 0;

    const dailyProductionReport = {
        slug: 'videos_produced',
        type: 'BAR',
        groupData: (data) => {
            const groups = _.range(1, nrOfDaysInMonth(filter.year, filter.month) + 1);
            return transformDataIntoGroups({
                showNrOfRowsInLabel: true, data, groups,
                isGroupMember: (group, row) => new Date(row.created).getDate() === group,
            });
        },
    };

    const dailyOpenedReport = {
        slug: 'videos_opened',
        type: 'BAR',
        groupData: (data) => {
            const groups = _.range(1, nrOfDaysInMonth(filter.year, filter.month) + 1);
            return transformDataIntoGroups({
                showNrOfRowsInLabel: true, data, groups,
                isGroupMember: (group, row) => new Date(row.created).getDate() === group,
            });
        },
    };

    const peakHoursReport = {
        slug: 'peak_viewing_hours',
        type: 'LINE',
        groupData: (data) => {
            const hours = Array.from({ length: 24 }, (_, i) => i);
            const counts = hours.map(() => 0);
            (data.rows || []).forEach(row => { counts[row.hour] = row.views; });
            return {
                data: {
                    labels: hours.map(h => `${h}h`),
                    datasets: [{ label: 'Videos', borderColor: '#083E58', backgroundColor: 'rgba(8,62,88,0.08)', data: counts, fill: true, pointRadius: 2 }],
                },
                options: { scales: { yAxes: [{ ticks: { beginAtZero: true, callback: v => v % 1 === 0 ? v : null } }] } },
            };
        },
    };

    const timeToFirstViewReport = {
        slug: 'time_to_first_view',
        type: 'BAR',
        groupData: (data) => {
            const buckets = [
                { label: '<1h', max: 1 }, { label: '1-4h', max: 4 }, { label: '4-12h', max: 12 },
                { label: '12-24h', max: 24 }, { label: '1-3d', max: 72 }, { label: '3-7d', max: 168 }, { label: '7d+', max: Infinity },
            ];
            const counts = buckets.map(() => 0);
            (data.rows || []).forEach(row => {
                const h = parseFloat(row.hours_to_first_view) || 0;
                const idx = buckets.findIndex(b => h < b.max);
                if (idx >= 0) counts[idx]++;
            });
            const total = counts.reduce((a, b) => a + b, 0);
            const pcts = counts.map(c => total > 0 ? Math.round(100 * c / total) : 0);
            const labels = buckets.map((b, i) => `${b.label} (${pcts[i]}%)`);
            return {
                data: { labels, datasets: [{ label: `${total} videos`, backgroundColor: '#083E58', data: pcts }] },
                options: { scales: { yAxes: [{ ticks: { beginAtZero: true, callback: v => `${v}%` } }] } },
            };
        },
    };

    const watchCompletionReport = {
        slug: 'watch_completion',
        type: 'DOUGHNUT',
        groupData: (data) => {
            const bucketOrder = ['0-25', '25-50', '50-75', '75-100'];
            const counts = bucketOrder.map(() => 0);
            (data.rows || []).forEach(row => { const idx = bucketOrder.indexOf(row.bucket); if (idx >= 0) counts[idx] += row.count; });
            return {
                data: {
                    labels: ['0-25%', '25-50%', '50-75%', '75-100%'],
                    datasets: [{ data: counts, backgroundColor: ['#40c4e0', '#2d97b5', '#1a6b8a', '#083E58'] }],
                },
            };
        },
    };

    const watchDistributionReport = {
        slug: 'videos_watched',
        type: 'BAR',
        groupData: (data) => {
            const stepSize = 10;
            const groups = _.range(0, 100, stepSize);
            const labels = groups.map(g => `${g}-${g + stepSize}%`);
            const counts = groups.map(() => 0);
            const watched = (data.rows || []).filter(row => row.percentage_watched > 0);
            watched.forEach(row => {
                const p = row.percentage_watched;
                const idx = Math.min(Math.floor(p / stepSize), groups.length - 1);
                counts[idx]++;
            });
            return {
                data: { labels, datasets: [{ label: `${watched.length} videos (watched > 0%)`, backgroundColor: '#083E58', data: counts }] },
                options: { scales: { yAxes: [{ ticks: { beginAtZero: true, callback: v => v % 1 === 0 ? v : null } }] } },
            };
        },
    };

    const repeatViewsReport = {
        slug: 'repeat_views',
        type: 'DOUGHNUT',
        groupData: (data) => {
            const bucketOrder = ['1 view', '2 views', '3 views', '4+ views'];
            const counts = bucketOrder.map(() => 0);
            (data.rows || []).forEach(row => { const idx = bucketOrder.indexOf(row.bucket); if (idx >= 0) counts[idx] = row.count; });
            const total = counts.reduce((a, b) => a + b, 0);
            const labels = bucketOrder.map((b, i) => total > 0 ? `${b} (${Math.round(100 * counts[i] / total)}%)` : b);
            return {
                data: {
                    labels,
                    datasets: [{ data: counts, backgroundColor: ['#083E58', '#1a6b8a', '#2d97b5', '#40c4e0'] }],
                },
            };
        },
    };

    const weekdayReport = {
        slug: 'weekday_distribution',
        type: 'BAR',
        groupData: (data) => {
            const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
            const counts = dayNames.map(() => 0);
            (data.rows || []).forEach(row => { if (row.dow >= 1 && row.dow <= 7) counts[row.dow - 1] = row.views; });
            const total = counts.reduce((a, b) => a + b, 0);
            const labels = dayNames.map((d, i) => total > 0 ? `${d} (${Math.round(100 * counts[i] / total)}%)` : d);
            return {
                data: {
                    labels,
                    datasets: [{ label: 'Videos', backgroundColor: '#083E58', data: counts }],
                },
                options: { scales: { yAxes: [{ ticks: { beginAtZero: true, callback: v => v % 1 === 0 ? v : null } }] } },
            };
        },
    };

    return (
        <div className="view">
            <Filters onFilterChange={onFilterChange} filter={filter} scenarios={scenarios?.scenarios} />
            <h1 className="heading">Statistics</h1>
            {isLoading ? (
                <Throbber title="Loading" />
            ) : error ? (
                <Error msg="Something went wrong loading statistics." />
            ) : (
                <div className="stats-dashboard">
                    {/* Tier 1: KPI cards */}
                    <div className="stats-kpis">
                        <KpiCard label="Produced" value={funnel.produced} />
                        <KpiCard label="Opened / loaded" value={funnel.opened} subtitle={`${openRate}% open rate`} />
                        <KpiCard label="Watched" value={funnel.watched} subtitle={`${watchedRate}% of opened`} />
                        <KpiCard label="Completed" value={funnel.completed} subtitle={`${completionRate}% of watched`} accent />
                    </div>

                    {/* Tier 2: Production & engagement overview */}
                    <div className="stats-row stats-row--3col">
                        <div className="stats-panel">
                            <h3 className="stats-panel__title">Videos produced per day</h3>
                            <ReportChart scenarios={scenarios?.scenarios} report={dailyProductionReport} filter={filter} />
                        </div>
                        <div className="stats-panel">
                            <h3 className="stats-panel__title">Videos opened per day</h3>
                            <ReportChart scenarios={scenarios?.scenarios} report={dailyOpenedReport} filter={filter} />
                        </div>
                        <div className="stats-panel">
                            <h3 className="stats-panel__title">Watch percentage distribution</h3>
                            <ReportChart scenarios={scenarios?.scenarios} report={watchDistributionReport} filter={filter} />
                        </div>
                    </div>

                    {/* Tier 3: Conversion & timing */}
                    <div className="stats-row stats-row--3col">
                        <div className="stats-panel">
                            <h3 className="stats-panel__title">Watch completion</h3>
                            <ReportChart scenarios={scenarios?.scenarios} report={watchCompletionReport} filter={filter} />
                        </div>
                        <div className="stats-panel">
                            <h3 className="stats-panel__title">Time to first view</h3>
                            <ReportChart scenarios={scenarios?.scenarios} report={timeToFirstViewReport} filter={filter} />
                        </div>
                        <div className="stats-panel">
                            <h3 className="stats-panel__title">Repeat views</h3>
                            <ReportChart scenarios={scenarios?.scenarios} report={repeatViewsReport} filter={filter} />
                        </div>
                    </div>

                    {/* Tier 4: Audience patterns */}
                    <div className="stats-row stats-row--2col">
                        <div className="stats-panel">
                            <h3 className="stats-panel__title">Peak viewing hours</h3>
                            <ReportChart scenarios={scenarios?.scenarios} report={peakHoursReport} filter={filter} />
                        </div>
                        <div className="stats-panel">
                            <h3 className="stats-panel__title">Weekday distribution</h3>
                            <ReportChart scenarios={scenarios?.scenarios} report={weekdayReport} filter={filter} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportsView;
